<template>
  <div class="markers-wrapper">
    <div 
      ref="canvasWrapper"
      :style="bgStyle"
    >
      <div v-for="item in propertyMarkers" :key="item._id">
        <div
          class="dot"
          :class="{inactive: activeElement && activeElement._id !== item._id}"
          :style="{
            left: item.marker.x * scale + 'px',
            top: item.marker.y * scale + 'px'
          }"
          @pointerdown="onMouseDown(item, $event)"
          @pointerup="onMouseUp($event)"
          @pointermove="onMouseDrag($event)"
        >
          <div class="dot__label">{{ item.label }}</div>
        </div>
      </div>

      <div v-for="item in [...subsectionMarkers, ...livingOptionMarkers]" :key="item._id">
        <tag-basic
          v-bind="item.marker"
          :class="{inactive: activeElement && activeElement._id !== item._id}"
          :pos="{ 
            x: item.marker.x,
            y: item.marker.y
          }"
          :label="{ en: item.label + '<br>now selling' }"
          :style="`transform: rotate(${item.marker.rotation}deg);`"
          @pointerdown.native="onMouseDown(item, $event)"
          @pointerup.native="onMouseUp($event)"
          @pointermove.native="onMouseDrag($event)"
        />
      </div>
      
      <reference-tag-slider 
        v-if="referenceTagsSlider.length > 0"
        :isVertical="true"
        :scaledAmount="scale"
        :tagRef="referenceTagsSlider"
        @click.native.stop="{}"
      />
    </div>
  </div>
</template>

<script>
import { orderBy } from 'lodash'
import { mapGetters } from 'vuex'
import TagBasic from '@/components/TagBasic'

export default {
  props: ['value', 'active-element', 'property-ids', 'subsection-ids', 'living-option-ids', 'background-image'],

  components: {
    TagBasic,
  },

  data () {
    return {
      scale: 1,
      draggableMarker: null,
    }
  },

  computed: {
    ...mapGetters([
      'activeLanguage',
      'page',
      'properties',
      'subsections',
      'livingOptions',
    ]),

    tags: {
      get () {
        return this.value
      }, 
      set (tags) {
        this.$emit('input', tags)
      }
    },

    propertyMarkers () {
      if (this.propertyIds) {
        return this.properties.filter(property => this.propertyIds.includes(property._id))
      }

      return []
    },

    subsectionMarkers () {
      if (this.subsectionIds) {
        return this.subsections.filter(subsection => this.subsectionIds.includes(subsection._id))
      }

      return []
    },

    livingOptionMarkers () {
      if (this.livingOptionIds) {
        return this.livingOptions.filter(option => this.livingOptionIds.includes(option._id) && option.active)
      }

      return []
    },

    bgStyle () {
      if (this.scale) {
        return {
          ['font-size']: (20 * this.scale) + 'px',
          ['height']: (1960 * this.scale) + 'px',
        }
      }
      return {}
    },

    referenceTagsSlider () {
      if (this.tags) {
        let refTags = this.tags.filter(function(tag) {
          return tag.type == "reference"
        })

        refTags = orderBy(refTags, ['pos.x'], ['asc'])

        if (this.dir === 'rtl') {
          let startingIndex = refTags.length

          refTags.forEach((tag, index) => {
            if (!tag.hideTagReference) {
              tag.referenceIndex = startingIndex--
            }
          })
          return refTags
        } else {
          refTags.forEach((tag, index) => {
            if (!tag.hideTagReference) {
              tag.referenceIndex = index + 1
            }
          })
          return refTags
        }
      } else {
        return []
      }
    }
  },

  mounted() {
    window.addEventListener('resize', this.getScaledAmount)
    this.$nextTick(() => {
      this.getScaledAmount()
    })
  },

  methods: {
    getScaledAmount () {
      // default size based on 3190x1960 - scale = 1
      if (this.$refs.canvasWrapper) {
        let canvasWidth = this.$refs.canvasWrapper.getBoundingClientRect().width
        let scaled = canvasWidth / 3190
        this.scale = scaled
      }
    },

    onMouseDrag(event) {      
      if (this.draggableMarker) {
        this.draggableMarker.x = Math.round(this.draggableMarker.x + event.movementX / this.scale)
        this.draggableMarker.y = Math.round(this.draggableMarker.y + event.movementY / this.scale)
      }
    },

    addTag () {
      this.$store.dispatch('GET_TAG_SCHEMA').then(res => {
        this.tags.push(res.data)
      })
    },

    openAssetLibrary(context, assetSelected) {
      this.$emit('openAssetLibrary', context, assetSelected)
    },

    onMouseDown (item, e) {
      e.target.setPointerCapture(e.pointerId)

      this.draggableMarker = item.marker

      this.$emit('selectedElement', item, 'marker')
    },
    
    onMouseUp (e) {
      e.target.releasePointerCapture(e.pointerId)
      this.draggableMarker = null
    },

    updateActiveDotPosition (e) {
      if (this.draggableMarker) {
        this.draggableMarker.pos.x = Math.round(this.draggableMarker.pos.x + e.movementX / this.scale)
        this.draggableMarker.pos.y = Math.round(this.draggableMarker.pos.y + e.movementY / this.scale)
      }
    },

    updatePolygon (targetItem, polygon) {
      targetItem.polygon = polygon
    },
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.getScaledAmount)
  }
}
</script>

<style lang="scss" scoped>
.markers-wrapper {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
}

.dot {
  border-radius: 50%;
  background-color: red;
  cursor: pointer;
  pointer-events: all;
  position: absolute;
  font-size: .75em;
  height: 3em;
  width: 3em;
  user-select: none;
  opacity: 1;
  transform: translate(-.25em, -.4em);
  z-index: 6;

  &__label {
    background: #fff;
    bottom: 3.5em;
    left: 50%;
    opacity: 0;
    padding: .5em;
    pointer-events: none;
    position: absolute;
    transform: translateX(-50%);
    width: max-content;
  }

  &:hover {
    .dot__label {
      opacity: 1;
    }
  }
}

.inactive {
  opacity: .5;
}
</style>
